import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  ArtifactRef,
  MediaPickerResult,
  MediaSourceId,
  SaveArtifact,
  Slot,
  artifactsEqual,
  isSlotLocked,
  slotDurationOf,
  slotStatus,
  usedDurationOf,
} from './editMediaTypes';
import {
  ChunkArtifact,
  GeneratedVideoChunk,
} from '../../../data/models/LibraryFile/GeneratedVideoChunk';

let slotKeyCounter = 0;
const nextSlotKey = () => `slot-${++slotKeyCounter}`;

// A locked slide spanning the whole chunk is the base layer the editable
// clips overlay/replace (locked slots are not even part of the save
// payload), so it must not compete for the chunk's duration.
const isBaseSlideSlot = (slot: Slot, chunkDuration: number) =>
  chunkDuration > 0 &&
  isSlotLocked(slot) &&
  slotDurationOf(slot) >= chunkDuration;

const isImageUri = (uri: string) => {
  const lower = uri.toLowerCase();
  return (
    lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png')
  );
};

const artifactToRef = (artifact: ChunkArtifact): ArtifactRef => {
  const duration = Math.max(0, Math.round(artifact.duration || 0));
  const fileDuration = Math.max(0, Math.round(artifact.totalFileDuration || 0));
  const startTime = Math.max(0, Math.floor(artifact.startTime || 0));
  const effectiveFileDuration = fileDuration > 0 ? fileDuration : duration;
  return {
    artifactId: artifact.id,
    source: 'library',
    libraryMediaFileId: artifact.libraryMediaFileId || null,
    storyBlocksVideoId: null,
    thumbnailUrl: artifact.publicUri,
    previewUrl: artifact.publicUri,
    fileDuration: effectiveFileDuration,
    kind: isImageUri(artifact.uri) ? 'image' : 'video',
    startTime,
    endTime: startTime + duration,
    isNotEditable: !!artifact.isNotEditable,
  };
};

const buildInitialSlots = (chunk: GeneratedVideoChunk | null): Slot[] => {
  if (!chunk?.artifacts) return [];
  const chunkDuration = Math.max(0, Math.round(chunk.duration || 0));
  const sortedArtifacts = [...chunk.artifacts].sort(
    (a, b) => a.order - b.order,
  );
  const slots: Slot[] = [];
  for (const artifact of sortedArtifacts) {
    const ref = artifactToRef(artifact);
    slots.push({slotKey: nextSlotKey(), original: ref, current: ref});
  }
  // fit raw artifact durations to the chunk duration — except when a locked
  // full-length slide is present: the other clips overlay it, so their raw
  // trims must not be crushed to make the sum match.
  const hasBaseSlide = slots.some(s => isBaseSlideSlot(s, chunkDuration));
  if (chunkDuration > 0 && slots.length > 0 && !hasBaseSlide) {
    const durationOf = (s: Slot) =>
      s.current ? Math.max(0, s.current.endTime - s.current.startTime) : 0;
    const totalDur = slots.reduce((sum, s) => sum + durationOf(s), 0);
    const drift = chunkDuration - totalDur;
    if (drift !== 0) {
      // don't absorb drift into locked slots (slide)
      const editable = slots
        .map((s, i) => ({s, i}))
        .filter(({s}) => !isSlotLocked(s));
      if (editable.length > 0) {
        // Preferred absorber first, then the rest by duration: a single slot
        // cannot always take the whole drift (each keeps at least 1s), so
        // the remainder cascades — otherwise the panel opens already showing
        // a bogus "clips exceed segment duration" warning.
        const ordered = chunk.useProvidedStartTimeAndDuration
          ? [...editable].reverse()
          : [...editable].sort((a, b) => durationOf(b.s) - durationOf(a.s));
        let remaining = drift;
        for (const {i} of ordered) {
          if (remaining === 0) break;
          const slot = slots[i];
          if (!slot.current) continue;
          const adjustedEnd = Math.max(
            slot.current.startTime + 1,
            slot.current.endTime + remaining,
          );
          const applied = adjustedEnd - slot.current.endTime;
          if (applied !== 0) {
            const adjustedRef = {...slot.current, endTime: adjustedEnd};
            slots[i] = {...slot, original: adjustedRef, current: adjustedRef};
            remaining -= applied;
          }
        }
      }
    }
  }
  return slots;
};

interface UseEditMediaStateArgs {
  shown: boolean;
  chunk: GeneratedVideoChunk | null;
}

export const useEditMediaState = ({shown, chunk}: UseEditMediaStateArgs) => {
  const [slots, setSlots] = useState<Slot[]>([]);
  const [originalSlots, setOriginalSlots] = useState<Slot[]>([]);
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);
  const [pending, setPending] = useState<MediaPickerResult | null>(null);
  const [activeSource, setActiveSource] = useState<MediaSourceId>('library');
  const [isReplaceAllMode, setIsReplaceAllMode] = useState(false);

  useEffect(() => {
    if (!shown || !chunk) return;
    const initial = buildInitialSlots(chunk);
    // Slide-generated chunks have no artifact rows; seed an empty slot so the
    // media picker is immediately usable instead of a dead panel.
    if (initial.length === 0 && Math.round(chunk.duration || 0) > 0) {
      initial.push({slotKey: nextSlotKey(), original: null, current: null});
    }
    setSlots(initial);
    setOriginalSlots(initial);
    const firstSelectable = initial.find(s => !isSlotLocked(s));
    setSelectedSlotKey(firstSelectable?.slotKey ?? null);
    setPending(null);
    setActiveSource('library');
    setIsReplaceAllMode(false);
  }, [shown, chunk?.id]);

  const chunkDuration = Math.max(0, Math.round(chunk?.duration ?? 0));

  const hasBaseSlide = useMemo(
    () => slots.some(s => isBaseSlideSlot(s, chunkDuration)),
    [slots, chunkDuration],
  );
  const usedDuration = useMemo(
    () =>
      Math.round(
        usedDurationOf(slots.filter(s => !isBaseSlideSlot(s, chunkDuration))),
      ),
    [slots, chunkDuration],
  );
  const freeDuration = Math.max(0, chunkDuration - usedDuration);

  const hasEmptySlot = slots.some(s => !s.current && !s.original);

  const canAddSlot = chunkDuration > 0 && freeDuration > 0 && !hasEmptySlot;

  useEffect(() => {
    if (!shown || !chunk) return;
    if (chunkDuration <= 0) return;
    if (freeDuration <= 0 && hasEmptySlot) {
      setSlots(prev =>
        prev.filter(s => s.original !== null || s.current !== null),
      );
      setSelectedSlotKey(prev => {
        const stillExists = slots.some(
          s =>
            s.slotKey === prev && (s.original !== null || s.current !== null),
        );
        if (stillExists) return prev;
        const fallback = slots.find(
          s => s.original !== null || s.current !== null,
        );
        return fallback?.slotKey ?? null;
      });
    }
  }, [shown, chunk?.id, chunkDuration, freeDuration, hasEmptySlot, slots]);

  const hasChanges = useMemo(() => {
    if (slots.length !== originalSlots.length) return true;
    return slots.some((s, i) => {
      const orig = originalSlots[i];
      if (!orig || s.slotKey !== orig.slotKey) return true;
      return !artifactsEqual(s.current, orig.current);
    });
  }, [slots, originalSlots]);

  const selectedSlot = slots.find(s => s.slotKey === selectedSlotKey) ?? null;

  const selectSlot = useCallback((key: string) => {
    setSelectedSlotKey(key);
    setPending(null);
  }, []);

  const applyPending = useCallback(() => {
    if (!pending || !selectedSlotKey) return;
    const target = slots.find(s => s.slotKey === selectedSlotKey);
    if (target && isSlotLocked(target)) return;
    setSlots(prev => {
      const otherUsed = prev
        .filter(
          s =>
            s.slotKey !== selectedSlotKey && !isBaseSlideSlot(s, chunkDuration),
        )
        .reduce((sum, s) => sum + slotDurationOf(s), 0);
      const availableForSlot = Math.max(0, chunkDuration - otherUsed);
      const pendingFileDuration = Math.max(
        0,
        Math.round(pending.fileDuration || 0),
      );
      return prev.map(s => {
        if (s.slotKey !== selectedSlotKey) return s;
        const previousDuration = s.current
          ? Math.max(0, s.current.endTime - s.current.startTime)
          : 0;
        const isImage = pending.kind === 'image';
        const fileCap =
          !isImage && pendingFileDuration > 0
            ? pendingFileDuration
            : Number.MAX_SAFE_INTEGER;
        const target =
          previousDuration > 0 ? previousDuration : Number.MAX_SAFE_INTEGER;
        const desiredEnd = Math.max(
          1,
          Math.floor(Math.min(fileCap, target, availableForSlot)),
        );
        return {
          ...s,
          current: {
            artifactId: s.current?.artifactId ?? s.original?.artifactId ?? null,
            source: pending.source,
            libraryMediaFileId: pending.libraryMediaFileId,
            storyBlocksVideoId: pending.storyBlocksVideoId,
            thumbnailUrl: pending.thumbnailUrl,
            previewUrl: pending.previewUrl,
            fileDuration: pendingFileDuration,
            kind: pending.kind,
            startTime: 0,
            endTime: desiredEnd,
          },
        };
      });
    });
    setPending(null);
  }, [pending, selectedSlotKey, chunkDuration]);

  const addEmptySlot = useCallback(() => {
    if (!canAddSlot) return;
    const newSlot: Slot = {
      slotKey: nextSlotKey(),
      original: null,
      current: null,
    };
    setSlots(prev => [...prev, newSlot]);
    setSelectedSlotKey(newSlot.slotKey);
    setPending(null);
  }, [canAddSlot]);

  const deleteSlot = useCallback(
    (key: string) => {
      setSlots(prev => {
        const slot = prev.find(s => s.slotKey === key);
        if (!slot) return prev;
        if (isSlotLocked(slot)) return prev;
        if (!slot.original) return prev.filter(s => s.slotKey !== key);
        return prev.map(s => (s.slotKey === key ? {...s, current: null} : s));
      });
      if (selectedSlotKey === key) {
        setSelectedSlotKey(prevKey => {
          const remaining = slots.filter(s => s.slotKey !== key);
          if (remaining.length === 0) return null;
          if (remaining.find(s => s.slotKey === prevKey)) return prevKey;
          return remaining[0]?.slotKey ?? null;
        });
        setPending(null);
      }
    },
    [selectedSlotKey, slots],
  );

  const revertSlot = useCallback((key: string) => {
    setSlots(prev =>
      prev.map(s => (s.slotKey === key ? {...s, current: s.original} : s)),
    );
    setPending(null);
  }, []);

  const setSlotTrim = useCallback(
    (key: string, startTime: number, endTime: number) => {
      setSlots(prev =>
        prev.map(s => {
          if (s.slotKey !== key || !s.current) return s;
          if (isSlotLocked(s)) return s;
          const isImage = s.current.kind === 'image';
          const fileDur = s.current.fileDuration;
          const startMax = isImage ? 0 : Math.max(0, fileDur - 1);
          const clampedStart = Math.max(
            0,
            Math.min(Math.round(startTime), startMax),
          );
          const endCap = isImage
            ? Math.max(1, chunkDuration || Number.MAX_SAFE_INTEGER)
            : fileDur;
          const minEnd = clampedStart + 1;
          const clampedEnd = Math.max(
            minEnd,
            Math.min(Math.round(endTime), endCap),
          );
          return {
            ...s,
            current: {
              ...s.current,
              startTime: clampedStart,
              endTime: clampedEnd,
            },
          };
        }),
      );
    },
    [chunkDuration],
  );

  const enterReplaceAllMode = useCallback(() => {
    setIsReplaceAllMode(true);
    setSelectedSlotKey(null);
    setPending(null);
  }, []);

  const exitReplaceAllMode = useCallback(() => {
    setIsReplaceAllMode(false);
    setPending(null);
    setSelectedSlotKey(prev => prev ?? slots[0]?.slotKey ?? null);
  }, [slots]);

  const reorderSlots = useCallback((fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    setSlots(prev => {
      const fromIdx = prev.findIndex(s => s.slotKey === fromKey);
      const toIdx = prev.findIndex(s => s.slotKey === toKey);
      if (fromIdx < 0 || toIdx < 0) return prev;
      if (isSlotLocked(prev[fromIdx]) || isSlotLocked(prev[toIdx])) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  }, []);

  const buildPayload = useCallback((): SaveArtifact[] => {
    return slots
      .filter(
        s =>
          s.current !== null &&
          !isSlotLocked(s) &&
          (s.current.artifactId !== null ||
            s.current.libraryMediaFileId !== null ||
            s.current.storyBlocksVideoId !== null),
      )
      .map((s, i) => {
        const ref = s.current!;
        const rawDuration = Math.max(0, ref.endTime - ref.startTime);
        const fitDuration =
          ref.kind === 'video' && ref.fileDuration > 0
            ? Math.max(0, ref.fileDuration - ref.startTime)
            : rawDuration;
        return {
          artifactId: ref.artifactId,
          libraryMediaFileId: ref.libraryMediaFileId,
          storyBlocksVideoId: ref.storyBlocksVideoId,
          order: i,
          startTime: ref.startTime,
          duration: Math.min(rawDuration, fitDuration),
        };
      });
  }, [slots]);

  return {
    slots,
    originalSlots,
    selectedSlot,
    selectedSlotKey,
    pending,
    activeSource,
    chunkDuration,
    usedDuration,
    freeDuration,
    canAddSlot,
    isDurationValid: chunkDuration > 0 && !hasEmptySlot,
    durationStatus:
      chunkDuration === 0
        ? ('match' as const)
        : usedDuration > chunkDuration
        ? ('over' as const)
        : // With a base slide the remainder of the chunk is the slide
        // itself, so shorter clips are not a shortfall.
        usedDuration < chunkDuration && !hasBaseSlide
        ? ('under' as const)
        : ('match' as const),
    durationDelta: usedDuration - chunkDuration,
    hasChanges,
    canApply:
      !!pending &&
      !!selectedSlotKey &&
      !(selectedSlot && isSlotLocked(selectedSlot)),
    slotStatusOf: slotStatus,
    setActiveSource,
    setPending,
    selectSlot,
    applyPending,
    addEmptySlot,
    deleteSlot,
    revertSlot,
    reorderSlots,
    setSlotTrim,
    buildPayload,
    isReplaceAllMode,
    enterReplaceAllMode,
    exitReplaceAllMode,
  };
};
